import { DrawerSlotsAndSlotProps } from '@mui/joy';
import { t } from 'i18next';
import { pick } from 'lodash';
import {
  CSSProperties,
  Dispatch,
  ReactNode,
  SetStateAction,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { FieldValues, UseFormReset } from 'react-hook-form';

import { ApiStatus, InputType, NotificationPosition } from 'core/enums';
import { useFocusInputFunctionality } from 'core/hooks';
import { useFormContext, useNotificationContext } from 'core/states';
import { ISubmitAction } from 'core/types';
import { focusFirstInvalidField, getFirstErrorMessageOnScreen } from 'core/utils';
import { Size } from 'theme/enums';

import FormView from './FormView';

export interface IFormProps {
  title?: string;
  subtitle?: string | JSX.Element;
  size?: Size;
  useGridLayout?: boolean;
  isOpen?: boolean;
  onClose?: (reset?: UseFormReset<FieldValues>) => void;
  submitBtnLabel?: string;
  cancelBtnLabel?: string;
  removeBtnLabel?: string;
  extraButtons?: ReactNode | ReactNode[];
  onSubmit?: (
    reset?: UseFormReset<FieldValues>,
    setError?: Dispatch<SetStateAction<string>>
  ) => (data: any) => void;
  onRemove?: (reset?: UseFormReset<FieldValues>) => void;
  disabledSubmit?: boolean;
  disabledCancel?: boolean;
  disableAutoClose?: boolean;
  // Opens without focusing the first input, e.g. an edit form that is read before it is changed.
  disableAutoFocus?: boolean;
  allowRemove?: boolean;
  children?: JSX.Element | JSX.Element[];
  getSubmitActions?: () => ISubmitAction[];
  interactive?: boolean;
  inline?: boolean;
  withoutButtons?: boolean;
  drawerSlotAndSlotProps?: DrawerSlotsAndSlotProps;
  level?: number;
  notificationPosition?: NotificationPosition;
  withoutNotification?: boolean;
  stickySxProps?: {
    header?: CSSProperties;
    notification?: CSSProperties;
  };
  fieldWrapperSxProps?: CSSProperties;
  shouldSubmitOnKeyDown?: boolean;
}

const getFirstInputChildName = (children: JSX.Element | JSX.Element[]): string | undefined => {
  const focusableInputTypes = Object.values(InputType).filter(
    (type) =>
      type !== InputType.CHECKBOX && type !== InputType.RADIO_GROUP && type !== InputType.SWITCH
  );

  const findFirstInputChild = (child: JSX.Element): string | undefined => {
    if (child?.props?.hidden) {
      return undefined;
    }

    if (
      child?.props?.name &&
      (!child?.props?.type || focusableInputTypes.includes(child.props.type))
    ) {
      return child.props.name;
    }

    if (child?.props?.children) {
      const childChildren = child.props.children;

      if (Array.isArray(childChildren)) {
        for (const grandChild of childChildren) {
          const result = findFirstInputChild(grandChild);
          if (result) {
            return result;
          }
        }
      } else if (childChildren) {
        const result = findFirstInputChild(childChildren);
        if (result) {
          return result;
        }
      }
    }

    return undefined;
  };

  if (Array.isArray(children)) {
    for (const child of children) {
      const result = findFirstInputChild(child);
      if (result) {
        return result;
      }
    }
  } else if (children) {
    return findFirstInputChild(children);
  }

  return undefined;
};

export const Form = ({
  title,
  subtitle,
  size,
  useGridLayout = false,
  isOpen,
  onClose,
  submitBtnLabel,
  cancelBtnLabel,
  removeBtnLabel,
  extraButtons,
  onSubmit,
  onRemove,
  disabledSubmit = false,
  disabledCancel = false,
  disableAutoClose = false,
  disableAutoFocus = false,
  allowRemove = false,
  children,
  getSubmitActions,
  interactive = false,
  inline = false,
  withoutButtons = false,
  drawerSlotAndSlotProps,
  level = 0,
  notificationPosition: customNotificationPosition,
  withoutNotification = false,
  stickySxProps,
  fieldWrapperSxProps,
  shouldSubmitOnKeyDown = true,
}: IFormProps) => {
  const [, setError] = useState('');
  const contentWrapperRef = useRef<HTMLDivElement>(null);
  const {
    clearDrawerNotification,
    clearFormNotification,
    notification,
    showNotification,
    getIsLoadingNotificationDisplayed,
  } = useNotificationContext();

  const { handleSubmit, control, errors, reset, setValue, getValues, focusRequest, isSubmitting } =
    useFormContext();
  const { setFocusElementName } = useFocusInputFunctionality();

  const notificationPosition = useMemo(() => {
    if (customNotificationPosition) {
      return customNotificationPosition;
    }

    return inline ? NotificationPosition.DRAWER : NotificationPosition.FORM;
  }, [customNotificationPosition, inline]);

  useEffect(() => {
    if (!isOpen) {
      return;
    }

    if (Object.keys(errors).length > 0) {
      showNotification({
        title: t('titleValidationError'),
        message: getFirstErrorMessageOnScreen(errors, contentWrapperRef.current ?? document),
        type: ApiStatus.ERROR,
        position: notificationPosition,
      });
    }
  }, [errors, isOpen, showNotification, notificationPosition]);

  // The request outlives a close without reset; reopening must not replay it.
  const handledFocusRequest = useRef(focusRequest);

  useEffect(() => {
    if (!isOpen || !focusRequest || focusRequest === handledFocusRequest.current) {
      return;
    }

    handledFocusRequest.current = focusRequest;

    focusFirstInvalidField(
      focusRequest.names ? pick(errors, focusRequest.names) : errors,
      contentWrapperRef.current ?? document
    );
    // Once per submit attempt: re-running on an errors change would move the cursor while typing.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [focusRequest, isOpen]);

  useEffect(() => {
    if (isOpen && !disableAutoFocus) {
      const firstInputChildName = getFirstInputChildName(children);

      setFocusElementName(firstInputChildName);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, setFocusElementName, disableAutoFocus]);

  const handleOnClose = (reset?: UseFormReset<FieldValues>) => {
    if (NotificationPosition.FORM === notification?.position) {
      clearFormNotification();
    }

    if (NotificationPosition.DRAWER === notification?.position) {
      clearDrawerNotification();
    }

    onClose(reset);
  };

  const disabledButtons = getIsLoadingNotificationDisplayed(notificationPosition);

  return (
    <FormView
      title={title}
      subtitle={subtitle}
      size={size}
      useGridLayout={useGridLayout}
      isOpen={isOpen}
      onSubmit={onSubmit}
      onClose={handleOnClose}
      onRemove={onRemove}
      submitBtnLabel={submitBtnLabel}
      cancelBtnLabel={cancelBtnLabel}
      removeBtnLabel={removeBtnLabel}
      extraButtons={extraButtons}
      disabledSubmit={disabledSubmit || disabledButtons}
      disabledCancel={disabledCancel || disabledButtons}
      disableAutoClose={disableAutoClose}
      allowRemove={allowRemove}
      // eslint-disable-next-line react/no-children-prop
      children={children}
      control={control}
      handleSubmit={handleSubmit}
      isSubmitting={isSubmitting}
      setValue={setValue}
      getValues={getValues}
      reset={reset}
      errors={errors}
      setError={setError}
      getSubmitActions={getSubmitActions}
      notificationPosition={notificationPosition}
      withoutNotification={withoutNotification}
      interactive={interactive}
      inline={inline}
      withoutButtons={withoutButtons}
      drawerSlotAndSlotProps={drawerSlotAndSlotProps}
      level={level}
      stickySxProps={stickySxProps}
      fieldWrapperSxProps={fieldWrapperSxProps}
      shouldSubmitOnKeyDown={shouldSubmitOnKeyDown}
      contentWrapperRef={contentWrapperRef}
    />
  );
};
